import React, { useMemo } from 'react';
import { Select, Space } from 'antd';

import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';

import useFormatMessage from 'hooks/useFormatMessage';
import Language from 'components/Language';
import { QuestionCircleFilled } from '@ant-design/icons';
import messages from './messages';

const QUERY = gql`
  query {
    languages {
      id
      name
      languageCode
      countryCode
      countryName      
      isCountryNameNeeded
      supported
    }
  }
`;

const LanguageSelect = ({
  onChange,
  value,
  include,
  exclude,
  allowOther,
  locale = true,
  ...rest
}) => {
  const $t = useFormatMessage();
  const { loading, data } = useQuery(QUERY, {
    fetchPolicy: 'cache-first',
  });

  const options = useMemo(() => {
    let languages = data?.languages || [];
    if (!locale) {
      languages = languages.reduce((acc, l) => {
        if (!acc.find(l2 => l2.id === l.languageCode)) {
          acc.push({
            id: l.languageCode,
            name: l.name,
            countryCode: null,
            countryName: null,
            supported: false,
          });
        }
        return acc;
      }, []);
    }

    languages = languages.filter(l => (!include || include.includes(l.id)) && (!exclude || !exclude.includes(l.id)));

    const mapLanguage = l => ({ value: l.id, filterValue: `${l.name.toLowerCase()} ${l.countryName?.toLowerCase()}`, label: <Language locale={l.id} languageName={l.name} countryName={l.isCountryNameNeeded && l.countryName} /> });

    const supportedLanguages = languages.filter(l => l.supported).map(mapLanguage);
    const otherLanguages = languages.filter(l => !l.supported).map(mapLanguage);
    let o;

    if (supportedLanguages.length && otherLanguages.length) {
      o = [{
        label: 'Supported languages (translations available)',
        options: supportedLanguages,
      }, {
        label: 'Other languages',
        options: otherLanguages,
      }];
    } else if (supportedLanguages.length) {
      o = supportedLanguages;
    } else {
      o = otherLanguages;
    }

    if (allowOther) {
      o.push({ value: '',
        filterValue: 'other',
        label: (
          <Space>
            <QuestionCircleFilled />
            Other
          </Space>
        ),
      });
    }
    return o;
  },
  [data, exclude, include, allowOther, locale]);

  return (
    <Select
      placeholder={$t(messages.chooseLanguage)}
      loading={loading}
      value={value}
      onChange={onChange}
      disabled={loading}
      showSearch
      optionFilterProp="filterValue"
      options={options}
      {...rest}
    />
  );
};

export default LanguageSelect;
